import { useStorage } from '@vueuse/core'
// see https://posthog.com/docs/libraries/vue-js
import posthog from 'posthog-js'
import { ref } from 'vue'

import { useOrganisation } from '@/composables/organisations'
import { useUser } from '@/composables/user'

const consentProvided = useStorage<boolean | null>('cookie-consent-provided', null)
const hasInitialised = ref(false)

// cookie vs non-cookie, see https://posthog.com/tutorials/cookieless-tracking
export function usePostHog() {
  const { profile } = useUser()
  const { currentOrg } = useOrganisation()

  function initPosthog() {
    if (hasInitialised.value) return
    const posthogKey = import.meta.env.VITE_APP_PUBLIC_POSTHOG_KEY
    const enableLocalPosthog = import.meta.env.VITE_APP_ENABLE_LOCAL_POSTHOG === 'true'
    const isLocalhost = ['localhost', '127.0.0.1'].includes(document.location.hostname)

    if (posthogKey && (enableLocalPosthog || !isLocalhost)) {
      posthog.init(posthogKey, {
        api_host: import.meta.env.VITE_APP_PUBLIC_POSTHOG_HOST,
        ui_host: 'https://us.posthog.com',
        capture_pageview: false, // we capture pageviews manually as a SPA - see router.ts
        persistence: consentProvided.value ? 'localStorage+cookie' : 'memory', // if consent not provided, we start with persisting posthog data to memory, until the user consents to cookie tracking (see CookieBanner.vue)
        // note we also start with screen recordings enabled, until cookie consent is denied - allows us to watch (anonymous) user behaviour before home page (i.e. registration / login flows)
      })
      hasInitialised.value = true
    }
  }

  function startTracking() {
    if (hasInitialised.value) {
      posthog.set_config({ persistence: 'localStorage+cookie' }) // see https://posthog.com/docs/libraries/js/persistence
      if (profile.value !== undefined) {
        // see https://posthog.com/docs/product-analytics/identify
        posthog.identify(profile.value.id, {
          email: profile.value.email,
          name: `${profile.value.firstName} ${profile.value.lastName}`,
          ...(currentOrg.value
            ? { organisationId: currentOrg.value.id, organisationName: currentOrg.value.name }
            : {}),
        })
      }
      // Consent can be granted after recording was stopped, so explicitly restart it.
      posthog.startSessionRecording()
    }
  }

  function stopTracking() {
    if (hasInitialised.value) {
      posthog.reset() // attempt to remove posthog.identify if that was called for some reason
      posthog.set_config({ persistence: 'memory' }) // see https://posthog.com/docs/libraries/js/persistence
      // see https://posthog.com/docs/session-replay/how-to-control-which-sessions-you-record
      posthog.stopSessionRecording()
    }
  }

  return {
    posthog,
    hasInitialised,
    initPosthog,
    startTracking,
    stopTracking,
  }
}
